import { flash, ImageUpload } from "@xola/ui-kit";
import React, { ReactNode } from "react";
import { useMutation } from "react-query";
import { Media } from "types";
import { uploadFile } from "@/api/mediaApi";

const ALLOWED_IMAGE_EXTENSION = /\.(jpg|jpeg|png|webp)$/i;

interface ImageUploadInputProps {
    uploadUrl: string;
    src?: string;
    size?: string;
    onUpdated?: (media?: Media) => any;
    onError: (error: string) => any;
    maxSize?: number;
    requirements?: ReactNode;
    hasDelete?: boolean;
    onDelete?: () => void;
    returnResponse?: boolean;
    setIsUploading?: (isUploading: boolean) => void;
}

export const ImageUploadInput = ({
    src,
    onUpdated,
    uploadUrl,
    onError,
    size = "large",
    maxSize,
    requirements,
    hasDelete,
    onDelete,
    returnResponse = false,
    setIsUploading,
}: ImageUploadInputProps) => {
    const { mutateAsync: updatePicture, isLoading } = useMutation(async (file: File) => {
        const response = await uploadFile(uploadUrl, file);

        if (returnResponse) {
            return response;
        }

        const data = response.data;

        return "media" in data ? data.media : data;
    });

    if (setIsUploading) {
        setIsUploading(isLoading);
    }

    const updatePictureFile = async (filePicture: File, errorMessage: string) => {
        try {
            if (filePicture?.size > 0 && !ALLOWED_IMAGE_EXTENSION.test(filePicture.name)) {
                flash.danger(`${filePicture.name} has an invalid extension. Only jpg, jpeg, png, webp are allowed`);
                return;
            }

            const uploadResult = await updatePicture(filePicture);
            if (onUpdated) {
                onUpdated(uploadResult as Media | undefined);
            }
        } catch {
            if (onError) {
                onError(errorMessage);
            } else {
                flash.danger(errorMessage);
            }
        }
    };

    const handlePictureChange = (filePicture: File) => {
        updatePictureFile(filePicture, "The image is not uploaded.");
    };

    const handleDeletePicture = () => {
        if (hasDelete && onDelete) {
            onDelete();
        }
    };

    return (
        <ImageUpload
            size={size}
            src={src}
            isLoading={isLoading}
            maxSize={maxSize}
            requirements={requirements}
            hasDelete={hasDelete}
            onChange={handlePictureChange}
            onDelete={handleDeletePicture}
            onError={onError}
        />
    );
};
